import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import QueryString from 'query-string'

import { composeCognitoError, validateUsername } from 'models/Authentication/utils'
import generalSelectors from 'models/General/selectors'
import authSelectors from 'models/Authentication/selectors'
import authActions from 'models/Authentication/actions'
import profileInfoSelectors from 'models/ProfileInfo/selectors'

import { STRING, EVENT_TYPE } from 'resources'
import { APP_URLS } from 'resources/urls'

import { sendAnalyticsEvent } from 'services/analytics'
import useSignInRedirect from 'hooks/useSignInRedirect'

import AppInformationText from 'components/AppInformationText'
import UnauthenticatedLayout, { UNAUTHENTICATED_BACKGROUND_KEYS } from 'components/UnauthenticatedLayout'
import ScribbleBackground from 'components/ScribbleBackground'
import ReklaimBackButton from 'components/ReklaimBackButton'

import {
  ContentContainer,
  Body,
  WelcomeTitle,
  WelcomeMessageContainer,
  WelcomeMessageText,
  WelcomeMessageRedText,
  Form,
  FormGroup,
  UsernameInput,
  SignInButton,
} from './styles'

const SignIn = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const [handleSignInRedirect] = useSignInRedirect()

  const region = useSelector(generalSelectors.getRegion)
  const isSignedIn = useSelector(authSelectors.getIsAuthenticated)
  const isProfileFetchSuccess = useSelector(profileInfoSelectors.statusSuccess)

  const [username, setUsername] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUnverifiedCheckFailed, setIsUnverifiedCheckFailed] = useState(false)
  const [composedCognitoError, setComposedCognitoError] = useState(null)

  useEffect(() => {
    sendAnalyticsEvent(EVENT_TYPE.logInView)
  }, [])

  const handleLogInSuccess = useCallback(() => {
    const { pathname: nextPath, search } = location?.state?.from || {}
    if (nextPath) {
      const combinedDeeplinkPath = QueryString.stringifyUrl({
        url: nextPath,
        query: QueryString.parse(search),
      })
      history.push(combinedDeeplinkPath)
    } else if ([APP_URLS.SIGN_IN, APP_URLS.SIGN_IN_VERIFY_CODE].includes(location.pathname)) {
      // If the page is already navigated away by Branch/Deep link, do nothing
      handleSignInRedirect()
    }
  }, [history, location, handleSignInRedirect])

  useEffect(() => {
    if (isSignedIn && isProfileFetchSuccess) {
      handleLogInSuccess()
    }
  }, [isSignedIn, isProfileFetchSuccess, handleLogInSuccess])

  const { isValid: isValidUsername, isEmail } = useMemo(() => validateUsername(username, region), [username, region])
  const isUsernameEmpty = !username
  const isInvalid = isUsernameEmpty || !isValidUsername || !!composedCognitoError || isUnverifiedCheckFailed

  let errorMessage = null
  if (!isUsernameEmpty && !isValidUsername) {
    errorMessage = STRING.signInPage.invalidUsernameMessage
  } else {
    errorMessage = isUnverifiedCheckFailed ? STRING.signInPage.unverifiedCheckErrorMessage : composedCognitoError
  }

  const handleChangeUsername = (event) => {
    setComposedCognitoError(null)
    setIsUnverifiedCheckFailed(false)
    setUsername(event.target.value)
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (isInvalid || isSubmitting) {
      return
    }
    sendAnalyticsEvent(EVENT_TYPE.logInSignInClick)
    setIsSubmitting(true)

    const nextUrl = location?.state?.from?.pathname
    dispatch(
      authActions.logInUser.init(username, isEmail, nextUrl, (isSuccess, message) => {
        setIsSubmitting(false)
        const type = isEmail ? 'EMAIL' : 'PHONE'
        if (isSuccess) {
          sendAnalyticsEvent(EVENT_TYPE.logInSubmit, { type, username })
          history.push({
            pathname: APP_URLS.SIGN_IN_VERIFY_CODE,
            state: location.state,
          })
        } else {
          sendAnalyticsEvent(EVENT_TYPE.logInFailure, { type, username, message })
          setComposedCognitoError(composeCognitoError(message))
        }
      }),
    )
  }

  return (
    <UnauthenticatedLayout backgroundKey={UNAUTHENTICATED_BACKGROUND_KEYS.peopleWithLogo}>
      <ScribbleBackground>
        <ReklaimBackButton />
        <ContentContainer>
          <Body>
            <Form onSubmit={handleSubmit}>
              <WelcomeTitle>{STRING.signInPage.welcomeTitleHeader}</WelcomeTitle>
              <WelcomeMessageContainer>
                <WelcomeMessageText>{STRING.signInPage.welcomeTitle.part1}</WelcomeMessageText>
                <WelcomeMessageRedText>{STRING.signInPage.welcomeTitle.part2}</WelcomeMessageRedText>
                <WelcomeMessageText>{STRING.signInPage.welcomeTitle.part3}</WelcomeMessageText>
              </WelcomeMessageContainer>
              <FormGroup>
                <UsernameInput
                  autoComplete="username"
                  data-testid="username-input"
                  placeholder={STRING.signInPage.placeholderUsername}
                  type="text"
                  value={username}
                  invalid={!!errorMessage}
                  onChange={handleChangeUsername}
                  disabled={isSubmitting}
                  autoFocus
                  errorMessage={errorMessage || null}
                />
              </FormGroup>
              <SignInButton
                buttonText={STRING.signInPage.loginButtonText}
                isLoading={isSubmitting}
                onPress={handleSubmit}
                disabled={isInvalid || isSubmitting}
                type="submit"
                data-testid="submit-button"
              />
            </Form>
          </Body>
          <AppInformationText />
        </ContentContainer>
      </ScribbleBackground>
    </UnauthenticatedLayout>
  )
}

export default SignIn
